/**
 * EmailReportForm Component
 *
 * Inline email input for sending the report to the customer.
 * Part of the post-decision conversion flow.
 */

import { useState } from 'react';
import { getOrCreateDeepLink, buildEmailMailtoUrl } from '../utils/deepLink';

interface EmailReportFormProps {
  /** Optional callback when email is sent */
  onEmailSent?: (email: string, reportType: 'compact' | 'full') => void;
  /** Show compact option (default: true) */
  showCompactOption?: boolean;
  /** Label text above the form */
  labelText?: string;
  /** Subtext below the label */
  subText?: string;
  /** Whether this is the primary CTA style */
  isPrimary?: boolean;
}

export function EmailReportForm({
  onEmailSent,
  showCompactOption = true,
  labelText = 'Email my report',
  subText = "We'll send you a copy so you can revisit this anytime or share it.",
  isPrimary = true,
}: EmailReportFormProps) {
  const [email, setEmail] = useState('');
  const [reportType, setReportType] = useState<'compact' | 'full'>('compact');
  const [isExpanded, setIsExpanded] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      return;
    }

    setStatus('sending');

    try {
      // Generate or get cached deep link (now async)
      const deepLink = await getOrCreateDeepLink();
      if (!deepLink) {
        console.error('[EmailReportForm] Failed to generate deep link - no link returned');
        setStatus('error');
        return;
      }

      // For now, use mailto: fallback
      // In production, this would call a backend email service
      const mailtoUrl = buildEmailMailtoUrl(email, deepLink.url, reportType);

      // Open mailto link
      window.location.href = mailtoUrl;

      setStatus('sent');
      onEmailSent?.(email, reportType);

      // Reset after delay
      setTimeout(() => {
        setStatus('idle');
        setEmail('');
        setIsExpanded(false);
      }, 3000);
    } catch (error) {
      console.error('[EmailReportForm] CRITICAL: Failed to persist document for email link:', error);
      setStatus('error');
      // Keep error state visible - don't auto-reset as quickly
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className={`email-report-trigger ${isPrimary ? 'primary' : 'secondary'}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          padding: 'var(--spacing-md) var(--spacing-lg)',
          background: isPrimary ? 'var(--color-primary)' : '#f8f9fa',
          color: isPrimary ? 'white' : 'var(--color-text)',
          border: isPrimary ? 'none' : '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          fontSize: 'var(--font-size-base)',
          fontWeight: 500,
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: '1.2em' }}>📧</span>
        <span>{labelText}</span>
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{
      background: '#f8f9fa',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--spacing-lg)',
    }}>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-xs)',
        }}>
          <span style={{ fontSize: '1.2em' }}>📧</span>
          <span style={{ fontWeight: 600, fontSize: 'var(--font-size-base)' }}>{labelText}</span>
        </div>
        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: 0,
        }}>
          {subText}
        </p>
      </div>

      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          style={{
            width: '100%',
            padding: 'var(--spacing-sm) var(--spacing-md)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-base)',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {showCompactOption && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-xs)',
          marginBottom: 'var(--spacing-md)',
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}>
            <input
              type="radio"
              name="reportType"
              checked={reportType === 'compact'}
              onChange={() => setReportType('compact')}
            />
            <span>Compact report (recommended)</span>
          </label>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}>
            <input
              type="radio"
              name="reportType"
              checked={reportType === 'full'}
              onChange={() => setReportType('full')}
            />
            <span>Full technical report</span>
          </label>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: 'var(--spacing-sm)',
      }}>
        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          style={{
            flex: 1,
            padding: 'var(--spacing-sm) var(--spacing-md)',
            background: 'white',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={status === 'sending' || !email.includes('@')}
          style={{
            flex: 2,
            padding: 'var(--spacing-sm) var(--spacing-md)',
            background: status === 'sent' ? '#28a745' : 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: status === 'sending' ? 'wait' : 'pointer',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500,
            opacity: (!email.includes('@') || status === 'sending') ? 0.6 : 1,
          }}
        >
          {status === 'sending' ? 'Opening email...' : status === 'sent' ? 'Email opened' : 'Send report'}
        </button>
      </div>

      {status === 'error' && (
        <p style={{
          marginTop: 'var(--spacing-sm)',
          fontSize: 'var(--font-size-xs)',
          color: '#dc3545',
          fontWeight: 'bold',
        }}>
          ⚠️ Failed to save report to database. Please try again or contact support if the problem persists.
        </p>
      )}
    </form>
  );
}

export default EmailReportForm;
